import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Check } from "lucide-react";
import OnboardingExit from "@/components/OnboardingExit";

const OPTIONS = [
  {
    code: "en",
    name: "English",
    sample: "Walk your life with awareness.",
    sub: "A contemplative companion.",
  },
  {
    code: "hi",
    name: "हिन्दी",
    sample: "जीवन को जागरूकता से चलें।",
    sub: "एक चिंतनशील साथी।",
  },
];

/**
 * Post-login, pre-onboarding language picker. Persisted server-side because the
 * user is about to fill profile details and we want those defaults + the
 * onboarding instructions in their preferred language.
 */
export default function OnboardingLanguage() {
  const navigate = useNavigate();
  const { lang, setLang } = useLang();
  const { user } = useAuth();
  const [selected, setSelected] = useState(lang);
  const [saving, setSaving] = useState(false);

  const greet = lang === "hi" ? "नमस्ते" : "Hello";
  const greetName = user?.name ? `, ${user.name.split(" ")[0]}` : "";

  const confirm = async () => {
    setSaving(true);
    setLang(selected);
    try { await api.put("/me/preferences", { language: selected }); } catch {}
    navigate("/onboarding/profile");
  };

  return (
    <div className="min-h-screen px-6 py-10 max-w-md mx-auto flex flex-col" style={{ backgroundColor: "var(--vedari-cream)", color: "var(--vedari-ink)" }}>
      <div className="flex justify-end mb-2">
        <OnboardingExit />
      </div>
      <div className="flex-1">
        <p className="text-caption uppercase tracking-[0.3em]" style={{ color: "var(--vedari-gold)" }}>
          {selected === "hi" ? "अपनी भाषा चुनें" : "Choose your language"}
        </p>
        <h1 className="font-display text-[40px] mt-3 leading-tight" data-testid="onboarding-language-title">
          {greet}{greetName}
        </h1>
        <p className="font-serif italic text-body-lg mt-4 leading-relaxed" style={{ color: "var(--vedari-ink-soft)" }}>
          {selected === "hi"
            ? "हम आगे कुछ निजी प्रश्न पूछेंगे। पहले बताइए — आप किस भाषा में चलना चाहते हैं?"
            : "We'll ask a few personal questions next. First — which language would you like to walk in?"}
        </p>

        <div className="mt-10 space-y-3" data-testid="language-options">
          {OPTIONS.map((opt) => {
            const isActive = selected === opt.code;
            return (
              <button
                key={opt.code}
                onClick={() => setSelected(opt.code)}
                data-testid={`language-option-${opt.code}`}
                className="w-full text-left rounded-xl p-5 transition-all active:scale-[0.99]"
                style={{
                  backgroundColor: isActive ? "rgba(184,149,74,0.10)" : "transparent",
                  border: `1.5px solid ${isActive ? "var(--vedari-gold)" : "var(--vedari-divider)"}`,
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-display text-2xl" style={{ color: isActive ? "var(--vedari-gold)" : "var(--vedari-ink)" }}>
                      {opt.name}
                    </p>
                    <p className="font-serif italic text-body-sm mt-2" style={{ color: "var(--vedari-ink-soft)" }}>
                      {opt.sample}
                    </p>
                    <p className="text-caption mt-1" style={{ color: "var(--vedari-ink-soft)" }}>
                      {opt.sub}
                    </p>
                  </div>
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-1"
                    style={{
                      backgroundColor: isActive ? "var(--vedari-gold)" : "transparent",
                      border: `1.5px solid ${isActive ? "var(--vedari-gold)" : "var(--vedari-divider)"}`,
                    }}
                  >
                    {isActive && <Check size={14} strokeWidth={2.5} style={{ color: "var(--vedari-cream)" }} />}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <p className="text-caption mt-8 font-serif italic" style={{ color: "var(--vedari-ink-soft)" }}>
          {selected === "hi"
            ? "आप कभी भी मेनू से भाषा बदल सकते हैं।"
            : "You can change the language anytime from the menu."}
        </p>
      </div>

      <div className="pt-6">
        <button
          onClick={confirm}
          disabled={saving}
          className="vedari-btn-primary w-full"
          data-testid="language-continue-btn"
        >
          {selected === "hi" ? "जारी रखें" : "Continue"}
        </button>
      </div>
    </div>
  );
}
